import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import {
  HashLocationStrategy,
  LocationStrategy,
  registerLocaleData,
} from '@angular/common';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { CustomHeaderInterceptor } from '@infrastructure/interceptors/CustomHeaderInterceptor';

import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import {
  PERFECT_SCROLLBAR_CONFIG,
  PerfectScrollbarConfigInterface,
  PerfectScrollbarModule,
} from 'ngx-perfect-scrollbar';

import { AppRoutingModule } from './app-routing.module';

import { AppComponent } from './app.component';
import { LoginModule } from '@infrastructure/shared/theme/login/login.module';

import {
  AvatarModule,
  BadgeModule,
  BreadcrumbModule,
  ButtonGroupModule,
  ButtonModule,
  CardModule,
  DropdownModule,
  FooterModule,
  FormModule,
  GridModule,
  HeaderModule,
  ListGroupModule,
  NavModule,
  ProgressModule,
  SharedModule,
  SidebarModule,
  TabsModule,
  UtilitiesModule,
  ModalModule,
} from '@coreui/angular-pro';
import { DatePipe } from '@angular/common';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import {
  BsDatepickerModule,
  BsDatepickerConfig,
} from 'ngx-bootstrap/datepicker';
import { IconModule, IconSetService } from '@coreui/icons-angular';

import { NuevoSocioComponent } from '@infrastructure/pages/catalog/socios/nuevo-socio/nuevo-socio.component';
import { environment } from 'environments/environment';
import { CapturaIncidenciasLoteComponent } from '@infrastructure/pages/captura/captura-incidencias-lote/captura-incidencias-lote.component';
import { AutorizaLiquidacionesComponent } from '@infrastructure/pages/proceso/autoriza-liquidaciones/autoriza-liquidaciones.component';
import { CalculaLiquidacionComponent } from '@infrastructure/pages/proceso/calcula-liquidacion/calcula-liquidacion.component';
import { TransferenciaUnionCreditoComponent } from '@infrastructure/pages/proceso/transferencia-union-credito/transferencia-union-credito.component';
import { ReversaLiquidacionComponent } from '@infrastructure/pages/captura/reversa-liquidacion/reversa-liquidacion.component';
import { ReversaLiquidacionService } from '@infrastructure/services/captura/reversa-liquidacion.service';
import { CierreSemanalComponent } from '@infrastructure/pages/proceso/cierre-semanal/cierre-semanal.component';
import { CalculaLiquidacionService } from '@infrastructure/pages/reportes/calcula-liquidacion.service';


import {
  DefaultAsideComponent,
  DefaultFooterComponent,
  DefaultHeaderComponent,
  DefaultLayoutComponent,
} from '@infrastructure/shared/theme';
import { PagesModule } from '@infrastructure/pages/pages/pages.module';
import { CustomMenuService } from '@application/services/curstomMenu.service';
import { AccountService } from '@application/services/account.service';
import { PermissionsService } from '@application/services/permissions.service';
import { SocioRepositoryService } from './infrastructure/repositories/socio-repository.service';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxMaskModule } from 'ngx-mask';
import { LoaderComponent } from './infrastructure/shared/theme/loader/loader.component';
import { LoaderService } from './infrastructure/shared/theme/loader/loader.service';
import localeEs from '@angular/common/locales/es-MX';
import { DecimalLimitDirective } from './domain/directives/decimal-limit.directive';
import { SelectedsModule } from '@infrastructure/shared/modules/selecteds/selecteds.module';
import { DatesService } from '@infrastructure/services/alpura/dates.service';
import { UtilInformationService } from '@infrastructure/services/utils/dates.service';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true,
};

function initializeKeycloak(keycloak: KeycloakService) {
  return () =>
    keycloak.init({
      config: {
        url: environment.keyclok,
        realm: environment.realm,
        clientId: environment.client,
      },
      initOptions: {
        onLoad: 'login-required',
        checkLoginIframe: true,
        pkceMethod: 'S256',
      },
    });
}

registerLocaleData(localeEs, 'es');

@NgModule({
  declarations: [
    DefaultAsideComponent,
    DefaultFooterComponent,
    DefaultHeaderComponent,
    DefaultLayoutComponent,
    AppComponent,
    NuevoSocioComponent,
    CapturaIncidenciasLoteComponent,
    AutorizaLiquidacionesComponent,
    TransferenciaUnionCreditoComponent,
    ReversaLiquidacionComponent,
    CierreSemanalComponent,
    LoaderComponent,
    DecimalLimitDirective
  ],

  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    AvatarModule,
    BreadcrumbModule,
    FooterModule,
    DropdownModule,
    GridModule,
    HeaderModule,
    SidebarModule,
    IconModule,
    PerfectScrollbarModule,
    NavModule,
    ButtonModule,
    FormModule,
    UtilitiesModule,
    ModalModule,
    ButtonGroupModule,
    ReactiveFormsModule,
    FormsModule,
    SidebarModule,
    SharedModule,
    TabsModule,
    ListGroupModule,
    ProgressModule,
    BadgeModule,
    ListGroupModule,
    CardModule,
    HttpClientModule,
    BsDatepickerModule.forRoot(),
    TimepickerModule,
    AutoCompleteModule,
    DatePipe,
    KeycloakAngularModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    PagesModule,
    LoginModule,
    SelectedsModule,
    NgbModule,
    NgxMaskModule.forRoot()
  ],
  providers: [
    ReversaLiquidacionService,
    BsDatepickerConfig,
    CalculaLiquidacionService,
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy,
    },
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomHeaderInterceptor,
      multi: true,
    },
    { provide: LOCALE_ID, useValue: 'es' },
    SocioRepositoryService,
    IconSetService,
    Title,
    DatePipe,
    CustomMenuService,
    DatesService,
    UtilInformationService,
    AccountService,
    PermissionsService,
    SocioRepositoryService,
    LoaderService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
